import React, { useState } from 'react';
import { ArrowLeft, CalendarDays, CreditCard, Edit3, Euro, Hash, Landmark, List, Plus, Receipt, Search } from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { nextInvoiceNumber } from '../../../../../services/InvoicesService';
import SalesReceiptForm from '../forms/SalesReceiptForm';
import { formatDate } from '../forms/FormParts';
import { PRESET_SALES_RECEIPTS, STORAGE_KEYS, SalesReceipt, formatEuro } from './financeMasters';
import { useStoredList, writeStoredList } from './useStoredList';
import '../../shared-modules.css';
import './FinanceMasters.css';

const SERIES = 'RV-2026';

const STATUS_BADGE: Record<SalesReceipt['status'], string> = {
  Borrador: 'draft',
  Emitido: 'paid'
};

const accountLabel = (code: string) => (code === '570000' ? '570000 · Caja' : '572000 · Bancos');

interface FinanceSalesReceiptsViewProps {
  privacyMode: boolean;
  onNavigate: (groupId: string, subScreenId?: string) => void;
  // Abre directamente el formulario de un recibo nuevo
  startNew?: boolean;
}

export const FinanceSalesReceiptsView = ({ privacyMode, onNavigate, startNew = false }: FinanceSalesReceiptsViewProps) => {
  const [receipts, setReceipts] = useStoredList<SalesReceipt>(STORAGE_KEYS.salesReceipts, PRESET_SALES_RECEIPTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState<SalesReceipt | null>(null);
  // Recibo abierto en el formulario ('new' si es nuevo)
  const [formReceipt, setFormReceipt] = useState<SalesReceipt | 'new' | null>(startNew ? 'new' : null);

  const closePanel = () => setSelectedReceipt(null);

  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedReceipt));

  const handleSave = (receipt: SalesReceipt) => {
    const exists = receipts.some(r => r.number === receipt.number);
    const next = exists ? receipts.map(r => (r.number === receipt.number ? receipt : r)) : [receipt, ...receipts];
    setReceipts(next);
    writeStoredList(STORAGE_KEYS.salesReceipts, next);
    setSelectedReceipt(startNew ? null : receipt);
  };

  const handleCloseForm = () => {
    if (startNew) onNavigate('deudores', 'recibos_venta');
    else setFormReceipt(null);
  };

  // Solo los borradores se editan: los emitidos ya tienen su asiento
  const handleEdit = (receipt: SalesReceipt) => {
    setSelectedReceipt(null);
    setFormReceipt(receipt);
  };

  if (formReceipt) {
    const editing = formReceipt === 'new' ? undefined : formReceipt;
    return (
      <div className="fin-master">
        {!startNew && (
          <div className="module-toolbar">
            <button type="button" className="qa-back-btn" onClick={() => setFormReceipt(null)}>
              <ArrowLeft size={14} /> Volver a recibos de venta
            </button>
          </div>
        )}
        <SalesReceiptForm
          privacyMode={privacyMode}
          receipt={editing}
          nextNumber={nextInvoiceNumber(receipts, `${SERIES}-`)}
          onSave={handleSave}
          onClose={handleCloseForm}
        />
      </div>
    );
  }

  const query = searchQuery.toLowerCase();
  const filteredReceipts = receipts.filter(r =>
    r.number.toLowerCase().includes(query) ||
    r.clientName.toLowerCase().includes(query) ||
    r.paymentMethod.toLowerCase().includes(query) ||
    r.status.toLowerCase().includes(query)
  );

  const columns: ColumnDef<SalesReceipt>[] = [
    {
      id: 'number',
      header: 'Nº RECIBO',
      render: (r) => <span className="fin-master__code">{r.number}</span>
    },
    {
      id: 'client',
      header: 'CLIENTE',
      render: (r) => (
        <div>
          <div className="fin-master__name">{r.clientName}</div>
          {r.clientCif && <div className="fin-master__sub">CIF: {r.clientCif}</div>}
        </div>
      )
    },
    {
      id: 'date',
      header: 'FECHA',
      render: (r) => <span className="fin-master__text">{formatDate(r.date)}</span>
    },
    {
      id: 'payment',
      header: 'FORMA DE PAGO',
      render: (r) => (
        <div>
          <div className="fin-master__text">{r.paymentMethod}</div>
          <div className="fin-master__sub">{accountLabel(r.targetAccount)}</div>
        </div>
      )
    },
    {
      id: 'total',
      header: 'TOTAL',
      align: 'right',
      render: (r) => <span className="fin-master__amount">{formatEuro(r.total, privacyMode)}</span>
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (r) => <span className={`module-badge ${STATUS_BADGE[r.status]}`}>{r.status}</span>
    }
  ];

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR Nº, CLIENTE, FORMA DE PAGO O ESTADO..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={() => onNavigate('deudores', 'crear_recibo_venta')} className="module-create-btn">
          <Plus size={14} /> Nuevo_Recibo
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedReceipt)}>
        <DataTable
          data={filteredReceipts}
          columns={columns}
          title={<><Receipt size={14} className="module-panel-title-icon" /> Recibos_De_Venta_Al_Contado</>}
          subtitle={`TOTAL: ${filteredReceipts.length}`}
          getRowId={(r) => r.number}
          onRowClick={(r) => setSelectedReceipt(r)}
          selectedRowId={selectedReceipt?.number}
          emptyText="No se encontraron recibos de venta."
          actionsHeader="ACCIONES"
          actionsColumn={(r) => (
            <RowActionsMenu
              actions={r.status === 'Borrador'
                ? [viewAction(() => setSelectedReceipt(r)), editAction(() => handleEdit(r))]
                : [viewAction(() => setSelectedReceipt(r))]}
            />
          )}
        />

        {selectedReceipt && (
          <DetailPanel
            expandable
            title="Ficha_De_Recibo_De_Venta"
            onClose={closePanel}
            headerActions={selectedReceipt.status === 'Borrador'
              ? <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEdit(selectedReceipt)} />
              : undefined}
            cards={
              <DetailCard icon={List} title={`Líneas (${selectedReceipt.lines.length})`}>
                <div className="fin-master__attachments">
                  {selectedReceipt.lines.map(l => (
                    <div key={l.id} className="fin-master__attachment">
                      <span>{l.description}</span>
                      <span className="fin-master__sub">
                        {l.quantity} × {formatEuro(l.price, privacyMode)} • IVA {l.vat}%
                      </span>
                    </div>
                  ))}
                </div>
              </DetailCard>
            }
          >
            <DetailIdentity
              icon={Receipt}
              reference={selectedReceipt.number}
              name={selectedReceipt.clientName}
              subtitle={<span className={`module-badge ${STATUS_BADGE[selectedReceipt.status]}`}>{selectedReceipt.status}</span>}
            />

            <DetailFieldList>
              <DetailField icon={Hash} label="CIF">{selectedReceipt.clientCif || '—'}</DetailField>
              <DetailField icon={CalendarDays} label="FECHA">{formatDate(selectedReceipt.date)}</DetailField>
              <DetailField icon={CreditCard} label="FORMA_DE_PAGO">{selectedReceipt.paymentMethod}</DetailField>
              <DetailField icon={Landmark} label="CUENTA_DESTINO">{accountLabel(selectedReceipt.targetAccount)}</DetailField>
              <DetailField icon={Euro} label="SUBTOTAL">{formatEuro(selectedReceipt.subtotal, privacyMode)}</DetailField>
              <DetailField icon={Euro} label="IVA">{formatEuro(selectedReceipt.vat, privacyMode)}</DetailField>
              <DetailField icon={Euro} label="TOTAL">{formatEuro(selectedReceipt.total, privacyMode)}</DetailField>
            </DetailFieldList>
          </DetailPanel>
        )}
      </MasterDetailLayout>
    </div>
  );
};

export default FinanceSalesReceiptsView;
